import type { ReactNode } from 'react'
import { XIcon } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface NotificationPanelProps {
	title?:           ReactNode
	count?:           number
	filterSlot?:      ReactNode
	sortSlot?:        ReactNode
	onMarkAllRead?:   () => void
	markAllLabel?:    string
	onClose?:         () => void
	children:         ReactNode
	emptyState?:      ReactNode
	className?:       string
}

export function NotificationPanel({
	title = 'Notifications',
	count,
	filterSlot,
	sortSlot,
	onMarkAllRead,
	markAllLabel = 'Mark all as read',
	onClose,
	children,
	emptyState,
	className,
}: NotificationPanelProps) {
	const isEmpty = !children || (Array.isArray(children) && children.length === 0)

	return (
		<div className={cn(
			'flex w-[22.5rem] flex-col gap-3 rounded-(--radius-lg) bg-(--color-popover) p-4 shadow-lg',
			className,
		)}>
			<div className="flex items-center gap-2">
				<div className="flex flex-1 items-center gap-2">
					<span className="text-base font-[500] text-(--color-foreground)">{title}</span>
					{typeof count === 'number' && (
						<span className="text-sm text-(--color-muted-foreground)">{count}</span>
					)}
				</div>
				{onClose && (
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="rounded p-1 text-(--color-muted-foreground) hover:text-(--color-foreground)"
					>
						<XIcon className="size-4" />
					</button>
				)}
			</div>

			{(filterSlot || sortSlot || onMarkAllRead) && (
				<div className="flex items-center gap-2">
					{filterSlot}
					{sortSlot}
					{onMarkAllRead && (
						<button
							type="button"
							onClick={onMarkAllRead}
							className="ml-auto text-sm font-[500] text-(--color-foreground) hover:opacity-80"
						>
							{markAllLabel}
						</button>
					)}
				</div>
			)}

			<div className="flex max-h-[32rem] flex-col gap-2 overflow-y-auto pr-1">
				{isEmpty ? emptyState : children}
			</div>
		</div>
	)
}
